import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { ComboBox, InlineLoading, TextInput } from '@carbon/react';
import { WarningFilled } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { useStore } from '@tanstack/react-store';
import { debounce } from 'lodash-es';

import { FreeTextComboBox } from 'components/free-text-combobox';

import { AfeWizardCtx } from '../context';
import {
  basinsLoadableAtom,
  countiesLoadableAtom,
  formationsLoadableAtom,
  operatorTypeaheadLoadableAtom,
  statesLoadableAtom,
} from '../atoms';
import type {
  BasinOption,
  CountyOption,
  FormationOption,
  StateOption,
  TypeaheadResult,
} from '../types';
import { filterCounty, filterState } from './util';
import { DnCSection } from './dnc-section';
import type { WellFormProps } from './existing-well-form';
import styles from './well-information-step.module.scss';

interface ImportedWellFormProps extends WellFormProps {
  onStateChange: (stateId: number | null) => void;
}

/**
 * Form fields for Imported Well type. Renders Well Name, Basin, State,
 * County, Operator (server typeahead), Formation, and conditional D&C section.
 * @param {ImportedWellFormProps} props - Basin/state change handlers and D&C flag
 * @return {JSX.Element} The imported well form fields
 */
const ImportedWellForm: FC<ImportedWellFormProps> = ({
  onBasinChange,
  onStateChange,
  showDcAndE,
}) => {
  const { form } = useContext(AfeWizardCtx)!;
  const [operatorState, searchOperator] = useAtom(
    operatorTypeaheadLoadableAtom
  );
  const [basinsLoadable, triggerBasins] = useAtom(basinsLoadableAtom);
  const [statesLoadable, triggerStates] = useAtom(statesLoadableAtom);
  const [countiesLoadable, triggerCounties] = useAtom(countiesLoadableAtom);
  const [formationsLoadable, triggerFormations] = useAtom(
    formationsLoadableAtom
  );
  const debouncedSearchOperator = useMemo(
    () => debounce(searchOperator, 400),
    [searchOperator]
  );

  useEffect(() => {
    return () => debouncedSearchOperator.cancel();
  }, [debouncedSearchOperator]);

  const operatorResults: TypeaheadResult[] =
    operatorState.state === 'hasData' && operatorState.data
      ? operatorState.data.results
      : [];
  const operatorLoading = operatorState.state === 'loading';
  const operatorError = operatorState.state === 'hasError';

  const basins: BasinOption[] =
    basinsLoadable.state === 'hasData' && basinsLoadable.data
      ? basinsLoadable.data
      : [];
  const loadingBasins = basinsLoadable.state === 'loading';
  const basinsError = basinsLoadable.state === 'hasError';

  const states: StateOption[] =
    statesLoadable.state === 'hasData' && statesLoadable.data
      ? statesLoadable.data
      : [];
  const loadingStates = statesLoadable.state === 'loading';
  const statesError = statesLoadable.state === 'hasError';

  const counties: CountyOption[] =
    countiesLoadable.state === 'hasData' && countiesLoadable.data
      ? countiesLoadable.data
      : [];
  const loadingCounties = countiesLoadable.state === 'loading';
  const countiesError = countiesLoadable.state === 'hasError';

  const formations: FormationOption[] =
    formationsLoadable.state === 'hasData' && formationsLoadable.data
      ? formationsLoadable.data
      : [];
  const loadingFormations = formationsLoadable.state === 'loading';
  const formationsError = formationsLoadable.state === 'hasError';

  const basinName = useStore(form.store, (s) => s.values.basinName);
  const stateId = useStore(form.store, (s) => s.values.stateId);
  const countyId = useStore(form.store, (s) => s.values.countyId);
  const operatorValue = useStore(form.store, (s) => s.values.operator);
  const reportedFormation = useStore(
    form.store,
    (s) => s.values.reportedFormation
  );

  const [formationsBasin, setFormationsBasin] = useState<
    string | null | undefined
  >(undefined);

  const selectedBasin = basinName
    ? (basins.find((b) => b.value === basinName) ?? {
        value: basinName,
        label: basinName,
      })
    : null;
  const selectedState = states.find((s) => s.id === stateId) ?? null;
  const selectedCounty = counties.find((c) => c.id === countyId) ?? null;
  const selectedFormation = reportedFormation
    ? (formations.find((f) => f.value === reportedFormation) ?? {
        value: reportedFormation,
        label: reportedFormation,
      })
    : null;

  // Seed items with current form value when no search results (e.g. remount)
  const operatorItems: TypeaheadResult[] =
    operatorResults.length > 0
      ? operatorResults
      : operatorValue
        ? [{ id: 0, value: operatorValue }]
        : [];
  const selectedOperator = operatorValue
    ? (operatorItems.find((i) => i.value === operatorValue) ?? null)
    : null;

  return (
    <div className={styles.formSection}>
      <form.Field name="importedWellName">
        {(field) => (
          <TextInput
            id="imported-well-name"
            labelText="Well Name *"
            value={field.state.value ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.handleChange(e.target.value || null)
            }
            className={styles.field}
          />
        )}
      </form.Field>
      {showDcAndE && <DnCSection />}
      <h3 className={styles.sectionHeading}>
        Additional Information (Optional)
      </h3>
      <div
        className={styles.typeaheadWrapper}
        onFocus={() => {
          if (basins.length === 0 && !loadingBasins) triggerBasins();
        }}
      >
        <FreeTextComboBox
          id="basin"
          key={`basin-${basinName ?? 'none'}`}
          titleText="Basin"
          items={basins}
          itemToString={(item) => (item as BasinOption | null)?.label ?? ''}
          selectedItem={selectedBasin}
          onChange={({ selectedItem }) => {
            const value = (selectedItem as BasinOption | null)?.value ?? null;
            if (value !== basinName) {
              onBasinChange(value);
            }
          }}
          placeholder="Select a basin"
          className={styles.field}
        />
        {loadingBasins && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {basinsError && <WarningFilled className={styles.errorIcon} />}
      </div>
      <div className={styles.fieldRow}>
        <div
          className={styles.typeaheadWrapper}
          onFocus={() => {
            if (states.length === 0 && !loadingStates) triggerStates();
          }}
        >
          <ComboBox
            id="state"
            key={`state-${stateId ?? 'none'}`}
            titleText="State"
            items={states}
            itemToString={(item: StateOption | null) => {
              return item ? `${item.name} (${item.abbreviation})` : '';
            }}
            selectedItem={selectedState}
            shouldFilterItem={(menu: {
              item: StateOption;
              inputValue: string | null;
            }) => filterState(menu, selectedState)}
            onChange={({ selectedItem }: any) => {
              onStateChange(selectedItem?.id ?? null);
              if (selectedItem?.id) triggerCounties(selectedItem.id);
            }}
            placeholder="Select a state"
            className={styles.field}
          />
          {loadingStates && (
            <InlineLoading description="" className={styles.spinner} />
          )}
          {statesError && <WarningFilled className={styles.errorIcon} />}
        </div>

        <div
          className={styles.typeaheadWrapper}
          onFocus={() => {
            if (stateId && counties.length === 0 && !loadingCounties)
              triggerCounties(stateId);
          }}
        >
          <ComboBox
            id="county"
            key={`county-${stateId ?? 'none'}-${countyId ?? 'none'}`}
            titleText="County"
            items={counties}
            itemToString={(item: CountyOption | null) => item?.shortName ?? ''}
            selectedItem={selectedCounty}
            shouldFilterItem={(menu: {
              item: CountyOption;
              inputValue: string | null;
            }) => filterCounty(menu, selectedCounty)}
            onChange={({ selectedItem }: any) => {
              form.setFieldValue('countyId', selectedItem?.id ?? null);
            }}
            placeholder="Select a county"
            disabled={!stateId}
            className={styles.field}
          />
          {loadingCounties && (
            <InlineLoading description="" className={styles.spinner} />
          )}
          {countiesError && <WarningFilled className={styles.errorIcon} />}
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.typeaheadWrapper}>
          <ComboBox
            id="operator"
            titleText="Operator"
            items={operatorItems}
            selectedItem={selectedOperator}
            itemToString={(item: TypeaheadResult | null) => item?.value ?? ''}
            onInputChange={(text: string) => {
              if (text && text !== form.state.values.operator)
                debouncedSearchOperator(text);
            }}
            onChange={({ selectedItem }: any) => {
              debouncedSearchOperator.cancel();
              form.setFieldValue('operator', selectedItem?.value ?? null);
            }}
            shouldFilterItem={() => true}
            placeholder="Search by operator..."
            className={styles.field}
          />
          {operatorLoading && (
            <InlineLoading description="" className={styles.spinner} />
          )}
          {operatorError && <WarningFilled className={styles.errorIcon} />}
        </div>

        <div
          className={styles.typeaheadWrapper}
          onFocus={() => {
            if (!loadingFormations && formationsBasin !== basinName) {
              setFormationsBasin(basinName);
              triggerFormations(basinName);
            }
          }}
        >
          <FreeTextComboBox
            id="formation"
            key={`formation-${basinName ?? 'none'}`}
            titleText="Reported Formation"
            items={formations}
            itemToString={(item) =>
              (item as FormationOption | null)?.label ?? ''
            }
            selectedItem={selectedFormation}
            onChange={({ selectedItem }) => {
              const value =
                (selectedItem as FormationOption | null)?.value ?? null;
              if (value !== reportedFormation) {
                form.setFieldValue('reportedFormation', value);
              }
            }}
            placeholder="Select a formation"
            className={styles.field}
          />
          {loadingFormations && (
            <InlineLoading description="" className={styles.spinner} />
          )}
          {formationsError && <WarningFilled className={styles.errorIcon} />}
        </div>
      </div>
    </div>
  );
};

export { ImportedWellForm };
